import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  OnDestroy,
  OnChanges,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RoleService } from '@d-services';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TabMenuModule } from 'primeng/tabmenu';
import { Subject, takeUntil } from 'rxjs';
import { BaseMenuItem, BaseTabMenuItem } from './tab-menu.interface';

@Component({
  selector: 'app-base-tab-menu',
  templateUrl: './tab-menu.component.html',
  styleUrls: ['./tab-menu.component.scss'],
  standalone: true,
  imports: [TabMenuModule, TranslateModule],
})
export class BaseTabMenuComponent implements OnInit, OnDestroy, OnChanges {
  @Input() items: BaseTabMenuItem[] = [];
  @Input() activeIndex!: number;
  @Input() activeId!: string;
  @Input() disabled = false;
  @Output() tabChanges = new EventEmitter<any>();

  tabMenuItems: BaseMenuItem[] = [];
  activeMenuItem!: BaseTabMenuItem;

  destroy$ = new Subject<boolean>();

  constructor(
    private translateService: TranslateService,
    private roleService: RoleService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this._buildMenuItem();

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => void this._buildMenuItem());
  }

  ngOnChanges(changes: SimpleChanges): void {
    const activeIndexChange: SimpleChange = changes['activeIndex'];
    const activeIdChange: SimpleChange = changes['activeId'];

    if (
      changes['items'] ||
      (activeIndexChange &&
        !activeIndexChange.firstChange &&
        activeIndexChange.previousValue !== activeIndexChange.currentValue) ||
      (activeIdChange &&
        !activeIdChange.firstChange &&
        activeIdChange.previousValue !== activeIdChange.currentValue)
    ) {
      this._buildMenuItem(changes['items'] ? this.items : null);
    }

    if (changes['disabled']) {
      this._buildMenuItem();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private _buildMenuItem(items?: BaseTabMenuItem[]) {
    if (!this.tabMenuItems.length || items)
      this.tabMenuItems = this.filterItemByPermission().map((item, index) => ({
        label: this.translateService.instant(item.label),
        id: item.id,
        disabled: item?.disabled || this.disabled,
        command: (event: any) => {
          if (event.originalEvent) {
            if (item.command) item.command({ id: item.id, index, item: item });
            else this.tabChanges.emit({ id: item.id, index, item: item });
          }
        },
        moduleCode: item.moduleCode,
      }));

    if (!this.tabMenuItems.length) {
      this.activeMenuItem = null;
      this.tabChanges.emit({ id: null, index: null, item: {} });
      return;
    }

    if (this.activeIndex >= 0) {
      this.activeMenuItem = this.tabMenuItems[this.activeIndex];
    }
    if (this.activeId) {
      const index = this.tabMenuItems.findIndex(
        (menu) => menu.id === this.activeId,
      );

      if (index >= 0) {
        this.activeMenuItem = this.tabMenuItems[index];
      } else {
        this.activeMenuItem = this.tabMenuItems[0];
        this.activeId = this.activeMenuItem.id;
      }
      this.activeMenuItem.command({
        id: this.activeMenuItem.id,
        index,
        item: this.activeMenuItem,
      });
    }
  }

  private filterItemByPermission(): BaseTabMenuItem[] {
    const action = this.route.snapshot.data['actionName'];
    return this.items.filter((m) => {
      if (!m.moduleCode) return true;
      return m.actionType || action
        ? this.roleService.checkPermissionByAction(
            m.moduleCode,
            m.actionType || action,
          )
        : this.roleService.getActionsByModuleCode(m.moduleCode);
    });
  }
}
